import { useState, useRef, useCallback } from 'react';
import { getModelSupportedInputs, InputType } from '../../chatModels';
import { useModels } from '../../contexts/ModelContext';
import { convertToJPEG } from '../../utils/imageUtils';

const generateAttachmentId = () =>
  `attachment-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

const determineAttachmentType = (fileType) => {
  switch (fileType) {
    case InputType.PNG:
    case InputType.JPEG:
      return 'image';
    case InputType.PDF:
      return 'pdf';
    case InputType.MP3:
    case InputType.WAV:
      return 'audio';
    case InputType.MP4:
    case InputType.MPEG:
    case InputType.MOV:
    case InputType.MPG:
    case InputType.WEBM:
      return 'video';
    default:
      if (fileType.startsWith('image/')) return 'image';
      if (fileType.startsWith('audio/')) return 'audio';
      if (fileType.startsWith('video/')) return 'video';
      return 'file';
  }
};

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const formatSupportedInputs = (inputs) =>
  inputs
    .map((type) => {
      switch (type) {
        case InputType.PNG:
          return 'PNG';
        case InputType.JPEG:
          return 'JPEG';
        case InputType.PDF:
          return 'PDF';
        case InputType.MP3:
          return 'MP3';
        case InputType.WAV:
          return 'WAV';
        case InputType.MP4:
          return 'MP4';
        case InputType.MPEG:
        case InputType.MPG:
          return 'MPEG';
        case InputType.MOV:
          return 'MOV';
        case InputType.WEBM:
          return 'WEBM';
        default:
          return type;
      }
    })
    .join(', ');

export const useFileHandling = (selectedModel) => {
  const { getModelByModelString } = useModels();
  const [attachments, setAttachments] = useState([]);
  const [isThinking, setIsThinking] = useState(false);
  const [isDeepResearch, setIsDeepResearch] = useState(false);

  const fileInputRef = useRef(null);

  const clearFileState = useCallback(() => {
    setAttachments([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const processFiles = useCallback(
    async (files) => {
      const fileArray = Array.from(files || []);
      if (fileArray.length === 0) return;

      const modelDetails = getModelByModelString(selectedModel);
      const supportedInputs = getModelSupportedInputs(modelDetails);
      const unsupportedFiles = [];
      const processedAttachments = [];

      for (const file of fileArray) {
        if (!file) continue;

        const fileType = file.type;
        const isSupported = supportedInputs.includes(fileType);

        if (!isSupported) {
          unsupportedFiles.push(file.name || 'Unknown file');
          continue;
        }

        try {
          const attachmentType = determineAttachmentType(fileType);
          let processedFile = file;
          let previewUrl = '';
          let mimeType = fileType;

          if (attachmentType === 'image') {
            processedFile = await convertToJPEG(file);
            mimeType = processedFile.type || InputType.JPEG;
            previewUrl = await fileToDataUrl(processedFile);
          } else {
            previewUrl = await fileToDataUrl(file);
          }

          processedAttachments.push({
            id: generateAttachmentId(),
            type: attachmentType,
            name: file.name,
            mimeType,
            size: processedFile.size,
            file: processedFile,
            previewUrl,
            url: previewUrl,
            isLocal: true,
          });
        } catch (error) {
          console.error('Error processing file', file?.name, error);
        }
      }

      if (unsupportedFiles.length > 0) {
        const readableTypes = formatSupportedInputs(supportedInputs);
        alert(
          `These files are not supported by the selected model: ${unsupportedFiles.join(
            ', '
          )}. Supported types: ${readableTypes || 'none'}.`
        );
      }

      if (processedAttachments.length > 0) {
        setAttachments((prev) => [...prev, ...processedAttachments]);
      }
    },
    [getModelByModelString, selectedModel]
  );

  const handleFileSelect = useCallback(
    async (event) => {
      await processFiles(event.target.files);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [processFiles]
  );

  const removeAttachment = useCallback((attachmentId) => {
    setAttachments((prev) => prev.filter((attachment) => attachment.id !== attachmentId));
    if (fileInputRef.current && fileInputRef.current.files?.length) {
      fileInputRef.current.value = '';
    }
  }, []);

  const triggerFileSelect = useCallback(() => {
    const modelDetails = getModelByModelString(selectedModel);
    const supportedInputs = getModelSupportedInputs(modelDetails);

    if (supportedInputs.length === 0) {
      alert('File uploading is not supported for this model.');
      return;
    }

    if (fileInputRef.current) {
      fileInputRef.current.accept = supportedInputs.join(',');
      fileInputRef.current.multiple = true;
      fileInputRef.current.click();
    }
  }, [getModelByModelString, selectedModel]);

  return {
    attachments,
    isThinking,
    isDeepResearch,
    setIsThinking,
    setIsDeepResearch,
    fileInputRef,
    handleFileSelect,
    processFiles,
    removeAttachment,
    clearFileState,
    triggerFileSelect
  };
};
